import { formatMoney } from "@/lib/money";
import { cn } from "@/lib/utils";
import { DateDisplay } from '@/components/date-display';
import { Check, X, AlertCircle, RefreshCw } from 'lucide-react';
import { TransactionStatusBadge } from './transaction-status-badge';
import { Badge } from '@/components/ui/badge';
import { CopyableResourceBadge } from '@/components/ui/copyable-resource-badge';
import { useTranslation } from 'react-i18next';

export interface TransactionItemData {
  id: string;
  type: string;
  status: string;
  amount: number;
  result?: string;
  createdAt: string;
  externalTransactionId?: string;
  authorizationCode?: string;
  processor?: {
    name: string;
    id: string;
  };
  error?: {
    code?: string;
    message?: string;
    processorCode?: string;
    processorMessage?: string;
  };
}

interface TransactionItemProps {
  transaction: TransactionItemData;
  currency: string;
  onClick?: (transaction: TransactionItemData) => void;
  isSelected?: boolean;
}

// Get status icon based on transaction type and result
function getStatusIcon(type: string, result?: string, status?: string) {
  if (type === 'refund') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/20">
        <RefreshCw className="h-4 w-4" />
      </div>
    );
  }

  if (result === 'approved' || status === 'succeeded') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-green-100 text-green-600 dark:bg-green-900/20">
        <Check className="h-4 w-4" />
      </div>
    );
  }

  if (result === 'declined' || status === 'declined' || status === 'rejected') {
    return (
      <div className="flex h-8 w-8 items-center justify-center rounded-full bg-red-100 text-red-600 dark:bg-red-900/20">
        <X className="h-4 w-4" />
      </div>
    );
  }

  return (
    <div className="flex h-8 w-8 items-center justify-center rounded-full bg-yellow-100 text-yellow-600 dark:bg-yellow-900/20">
      <AlertCircle className="h-4 w-4" />
    </div>
  );
}

// Format transaction type for display
function formatTransactionType(type: string, t: (key: string) => string): string {
  const typeMap: Record<string, string> = {
    purchase: t('types.purchase'),
    crypto_sale: t('types.crypto_sale'),
    refund: t('types.refund'),
    authorization: t('types.authorization'),
    capture: t('types.capture'),
    void: t('types.void'),
    sale: t('types.sale'),
    auth: t('types.auth'),
    redirect_to_payment: 'Redirect To Payment',
    threeds: '3D Secure',
  };
  return typeMap[type] || type.charAt(0).toUpperCase() + type.slice(1);
}

export function TransactionItem({ transaction, currency, onClick, isSelected }: TransactionItemProps) {
  const { t: tTransactions } = useTranslation('transactions');
  const { t: tCommon } = useTranslation('common');

  const handleClick = () => {
    if (onClick) {
      onClick(transaction);
    }
  };

  return (
    <div
      className={cn(
        'group flex items-start gap-3 rounded-lg border p-3 pr-10 transition-colors',
        onClick && 'hover:bg-muted/50 cursor-pointer',
        isSelected && 'bg-muted/50',
      )}
      onClick={handleClick}
    >
      {/* Status Icon */}
      <div className="mt-0.5">{getStatusIcon(transaction.type, transaction.result, transaction.status)}</div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        {/* Header: Type, Status, Amount */}
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-2">
            <h4 className="text-sm font-medium">
              {formatTransactionType(transaction.type, tTransactions)}
            </h4>
            <TransactionStatusBadge status={transaction.status} result={transaction.result} />
          </div>

          {/* Amount */}
          <span
            className={cn(
              'whitespace-nowrap text-sm font-semibold',
              transaction.type === 'refund'
                ? 'text-red-600 dark:text-red-400'
                : 'text-green-600 dark:text-green-400',
            )}
          >
            {transaction.type === 'refund' ? '-' : '+'}
            {formatMoney(transaction.amount, currency)}
          </span>
        </div>

        {/* Metadata */}
        <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs">
          <time><DateDisplay value={transaction.createdAt} /></time>

          {transaction.processor && (
            <>
              <span>•</span>
              <Badge variant="outline" className="text-xs">
                {transaction.processor.name}
              </Badge>
            </>
          )}

          {transaction.authorizationCode && (
            <>
              <span>•</span>
              <span>{tTransactions('labels.authCode')}: {transaction.authorizationCode}</span>
            </>
          )}

          {transaction.error && (
            <>
              <span>•</span>
              <Badge variant="destructive" className="text-xs">
                {transaction.error.code || tCommon('error')}
              </Badge>
            </>
          )}
        </div>

        {/* Error message */}
        {transaction.error?.message && (
          <div className="mt-1 text-xs text-red-600 dark:text-red-400">{transaction.error.message}</div>
        )}

        {/* Transaction IDs */}
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <CopyableResourceBadge
            name={tTransactions('labels.transaction')}
            value={transaction.id}
            variant="outline"
          />
          {transaction.externalTransactionId && (
            <CopyableResourceBadge
              name={tTransactions('labels.externalId')}
              value={transaction.externalTransactionId}
              variant="outline"
            />
          )}
        </div>
      </div>
    </div>
  );
}
